import { z } from 'zod';

/**
 * DIAGNOSTICOS
 *
 * Codigo ESTABLE en SCREAMING_SNAKE_CASE. Nunca texto libre como identificador:
 * el texto se traduce (gl/es/en) y cambia; el codigo es el contrato entre el
 * aviso de pantalla y el rechazo de produccion.
 *
 * DEC-10: este mismo paquete corre en el navegador (respuesta instantanea) y en
 * el servidor (autoridad). Ambos deben producir EXACTAMENTE los mismos codigos
 * sobre los mismos vectores, o el build falla.
 */
export const DIAGNOSTIC_CODES = [
  // Geometria
  'LAYER_OUTSIDE_ZONE',
  'LAYER_OUTSIDE_CANVAS',
  'DESIGN_AREA_EXCEEDED',
  'ZONE_TECHNIQUE_NOT_ALLOWED',
  // Imagen
  'EFFECTIVE_DPI_TOO_LOW',
  'IMAGE_UPSCALED',
  // Tintas y color
  'TOO_MANY_INKS',
  'GRADIENT_NOT_ALLOWED',
  'TECHNICAL_WHITE_NOT_AVAILABLE',
  'COLOR_TYPE_NOT_SUPPORTED',
  // Trazo y texto
  'STROKE_TOO_THIN',
  /** Texto en arco que da mas de una vuelta: las letras se pisan. */
  'ARC_OVERLAP',
  'TEXT_TOO_SMALL',
  'TEXT_NOT_FROZEN',
  'GEOMETRY_ESTIMATED',
  // Soporte y proceso
  'SUBSTRATE_NOT_SUPPORTED',
  'EXTERNAL_DIGITIZING_REQUIRED',
  // Documento
  'EMPTY_DESIGN',
  'LAYER_LIMIT_EXCEEDED',
] as const;

export type DiagnosticCode = (typeof DIAGNOSTIC_CODES)[number];

export const Severity = z.enum(['error', 'warning', 'info']);
export type Severity = z.infer<typeof Severity>;

export const Diagnostic = z.object({
  code: z.enum(DIAGNOSTIC_CODES),
  severity: Severity,
  /** Capa afectada, si el diagnostico es de una capa concreta. */
  layerId: z.string().optional(),
  /** Valor MEDIDO (lo que hay). */
  measured: z.number().optional(),
  /** Valor ESPERADO por la regla (el limite). */
  expected: z.number().optional(),
  /** Unidad de `measured`/`expected`, para poder traducir el mensaje. */
  unit: z.enum(['mm', 'mm2', 'dpi', 'count', 'none']).default('none'),
  /** Regla que genero el diagnostico: `<techniqueId>@<version>` o `zone`. */
  ruleId: z.string(),
  /** Datos extra para componer el mensaje traducido. Solo primitivos. */
  params: z.record(z.union([z.string(), z.number(), z.boolean()])).optional(),
});
export type Diagnostic = z.infer<typeof Diagnostic>;

export interface ValidationResult {
  diagnostics: Diagnostic[];
  /** true si NO hay ningun diagnostico de severidad `error`. */
  valid: boolean;
  /** Resumen util para la UI. */
  counts: { error: number; warning: number; info: number };
}

export function summarize(diagnostics: Diagnostic[]): ValidationResult {
  const counts = { error: 0, warning: 0, info: 0 };
  for (const d of diagnostics) counts[d.severity] += 1;
  return { diagnostics, valid: counts.error === 0, counts };
}

/**
 * Orden ESTABLE de diagnosticos: severidad, luego codigo, luego capa. Sin esto,
 * navegador y servidor podrian emitir el mismo conjunto en distinto orden y la
 * prueba de paridad seria un falso negativo.
 */
const SEVERITY_ORDER: Record<Severity, number> = { error: 0, warning: 1, info: 2 };

export function sortDiagnostics(diagnostics: Diagnostic[]): Diagnostic[] {
  return [...diagnostics].sort((a, b) => {
    const s = SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
    if (s !== 0) return s;
    if (a.code !== b.code) return a.code < b.code ? -1 : 1;
    const al = a.layerId ?? '';
    const bl = b.layerId ?? '';
    if (al !== bl) return al < bl ? -1 : 1;
    return 0;
  });
}
